import { Separator } from "@/components/ui/separator";
import NewsSection from "@/components/auth/NewsSection";
import AuthCard from "@/components/auth/AuthCard";
import RainBackground from "@/components/auth/RainBackground";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileNewsSection from "@/components/auth/MobileNewsSection";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  buildRedirectAwarePath,
  getSafeRedirectOrDefault,
} from "@/utils/redirectUtils";
import { authLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import NativeAppSkeleton from "@/components/native/NativeAppSkeleton";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { toast } from "sonner";
import { UserType } from "@/types/user";
import { AuthService } from "@/services/auth/authService";
import { AuthContext } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { saveNativeTokens, getNativeTokens } from "@/integrations/clients/carAuction/nativeAuth";
import { resetNativeAuthClientState } from "@/integrations/clients/carAuction/carAuctionClient";
import { closeBankIdSystemBrowser } from "@/lib/native/bankIdBrowser";
import { buildNativeAppAuthDeepLink } from "@/lib/native/parseAppUrl";
import {
  peekPendingBankIdReturn,
  takePendingBankIdReturn,
} from "@/hooks/useNativeDeepLinks";
import { UserService } from "@/services/user/userService";
import { mapApiUserTypeToUserType } from "@/utils/authUtils";

type BankIdErrorReason =
  | "no_account"
  | "registration_pending"
  | "unavailable"
  | string;
type BankIdRegisterErrorReason =
  | "email_exists"
  | "email_pending"
  | "ssn_exists"
  | "ssn_pending"
  | "unavailable"
  | string;

const CONSUMED_NATIVE_CODES_KEY = "bilenia.nativeBankId.consumedCodes";
const BANKID_LOGIN_TOAST_KEY = "bilenia.nativeBankId.loginToast";
const BANKID_REGISTER_TOAST_KEY = "bilenia.nativeBankId.registerToast";

/** Deduplicate parallel nativeCode exchanges (Strict Mode / remount / double Intent). */
const inflightNativeCodeExchanges = new Map<
  string,
  Promise<{
    success: boolean;
    flow: "login" | "register";
    nativeTokens: { accessToken: string; refreshToken: string };
  }>
>();

function readConsumedNativeCodes(): string[] {
  try {
    const raw = sessionStorage.getItem(CONSUMED_NATIVE_CODES_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter((c): c is string => typeof c === "string")
      : [];
  } catch {
    return [];
  }
}

function isNativeCodeConsumed(code: string): boolean {
  return readConsumedNativeCodes().includes(code);
}

function markNativeCodeConsumed(code: string) {
  try {
    const next = [...readConsumedNativeCodes().filter((c) => c !== code), code].slice(
      -20,
    );
    sessionStorage.setItem(CONSUMED_NATIVE_CODES_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function wasBankIdToastShown(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markBankIdToastShown(key: string) {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

const Auth = () => {
  const isMobile = useIsMobile();
  const { isAuthenticated, isLoading, isNativeSessionPending, user } = useAuth();
  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get("redirect");
  const postAuthRedirect = getSafeRedirectOrDefault(redirectParam);
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const labels = authLabels(language);
  const bankIdSuccessToastShown = useRef(false);
  const bankIdRegisterToastShown = useRef(false);
  // Survive URL cleanup of ?bankidError= so leftover cookies cannot auto-redirect.
  const initialBankIdError = searchParams.get("bankidError");
  const initialBankIdRegisterError = searchParams.get("bankidRegisterError");
  const initialBankIdRegister = searchParams.get("bankidRegister");
  const bankIdLoginFailed = useRef(Boolean(initialBankIdError));
  const bankIdRegisterFailed = useRef(Boolean(initialBankIdRegisterError));
  const [bankIdErrorReason, setBankIdErrorReason] = useState<BankIdErrorReason | null>(
    initialBankIdError,
  );
  const [bankIdErrorOpen, setBankIdErrorOpen] = useState(Boolean(initialBankIdError));
  const [bankIdRegisterErrorReason, setBankIdRegisterErrorReason] =
    useState<BankIdRegisterErrorReason | null>(initialBankIdRegisterError);
  const [bankIdRegisterErrorOpen, setBankIdRegisterErrorOpen] = useState(
    Boolean(initialBankIdRegisterError),
  );
  const [bankIdRegisterPendingOpen, setBankIdRegisterPendingOpen] = useState(
    initialBankIdRegister === "pending",
  );
  const nativeCodeHandled = useRef(false);
  // Deduplicate handling of the *same* deep-link search string; reset when params clear
  // so a later identical error (e.g. no_account again) still opens the dialog.
  const lastHandledBankIdSearch = useRef<string | null>(null);
  const webNativeCode = !isNative ? searchParams.get("nativeCode") : null;
  // iOS native HTTPS bridge sets nativeReturn=1 (and usually nativeCode on success).
  // Do NOT treat plain web BankID ?bankid=success / ?bankidError= as app returns.
  const webNativeReturn =
    !isNative &&
    (Boolean(webNativeCode) || searchParams.get("nativeReturn") === "1");
  const [openInAppPromptOpen, setOpenInAppPromptOpen] = useState(webNativeReturn);
  const autoDeepLinkAttempted = useRef(false);

  // iOS same-device BankID often returns in Safari (HTTPS bridge). Auto-open the app
  // via custom scheme; keep the dialog as a manual fallback.
  useEffect(() => {
    if (isNative || !webNativeReturn) {
      autoDeepLinkAttempted.current = false;
      return;
    }
    setOpenInAppPromptOpen(true);
    if (autoDeepLinkAttempted.current) return;
    autoDeepLinkAttempted.current = true;
    const deepLink = buildNativeAppAuthDeepLink(location.search);
    window.location.href = deepLink;
  }, [isNative, webNativeReturn, location.search]);

  // Cold start: deep link may be stashed before Auth mounts, or bootstrap raced.
  // Re-apply pending BankID query onto /auth so exchange / error dialogs run.
  useEffect(() => {
    if (!isNative) return;
    // Only treat the hash as complete when nativeCode / error is already present.
    // Do NOT clear stash on bare ?bankid=success — that can drop a pending nativeCode.
    if (
      searchParams.get("nativeCode") ||
      searchParams.get("bankidError") ||
      searchParams.get("bankidRegisterError")
    ) {
      if (peekPendingBankIdReturn()) takePendingBankIdReturn();
      return;
    }
    const pending = peekPendingBankIdReturn();
    if (!pending) return;
    const taken = takePendingBankIdReturn();
    if (!taken) return;
    console.info("[BankID] restoring pending return", taken);
    navigate(
      { pathname: "/auth", search: taken.startsWith("?") ? taken : `?${taken}` },
      { replace: true },
    );
  }, [isNative, navigate, location.search, searchParams]);

  /**
   * Native deep links often land on /auth while Auth is already mounted (warm return).
   * useState(initial…) does not re-run — watch location.search and open error dialogs.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loginErr = params.get("bankidError");
    const registerErr = params.get("bankidRegisterError");
    const registerStatus = params.get("bankidRegister");
    const hasBankIdReturnParam = Boolean(
      loginErr || registerErr || registerStatus === "pending",
    );

    if (!hasBankIdReturnParam) {
      lastHandledBankIdSearch.current = null;
      return;
    }

    // Same navigation already processed (effect re-run / Strict Mode)
    if (lastHandledBankIdSearch.current === location.search) {
      return;
    }
    lastHandledBankIdSearch.current = location.search;

    if (loginErr) {
      bankIdLoginFailed.current = true;
      setBankIdErrorReason(loginErr);
      setBankIdErrorOpen(true);
      authContext?.logout();
      queryClient.clear();
      void AuthService.logout().catch(() => undefined);
    }

    if (registerErr) {
      bankIdRegisterFailed.current = true;
      setBankIdRegisterErrorReason(registerErr);
      setBankIdRegisterErrorOpen(true);
      authContext?.logout();
      queryClient.clear();
      void AuthService.logout().catch(() => undefined);
    }

    if (registerStatus === "pending") {
      setBankIdRegisterPendingOpen(true);
    }

    const next = new URLSearchParams(location.search);
    next.delete("bankidError");
    next.delete("bankidRegisterError");
    next.delete("t");
    if (registerStatus === "pending") next.delete("bankidRegister");
    const nextSearch = next.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authContext/queryClient stable enough; keyed on search
  }, [location.search, location.pathname, navigate]);

  // Native BankID return: exchange one-time code → nativeTokens → session
  useEffect(() => {
    if (!isNative) return;
    const params = new URLSearchParams(location.search);
    const nativeCode = params.get("nativeCode");
    if (!nativeCode) {
      nativeCodeHandled.current = false;
      return;
    }
    if (nativeCodeHandled.current) return;
    nativeCodeHandled.current = true;

    // Replay after Back / remount: skip exchange, clear query, stay logged in.
    if (isNativeCodeConsumed(nativeCode)) {
      bankIdSuccessToastShown.current = true;
      bankIdRegisterToastShown.current = true;
      if (getNativeTokens()?.accessToken) {
        navigate(postAuthRedirect, { replace: true });
      } else {
        const next = new URLSearchParams(location.search);
        next.delete("nativeCode");
        next.delete("bankid");
        next.delete("bankidRegister");
        next.delete("t");
        navigate(
          {
            pathname: location.pathname,
            search: next.toString() ? `?${next.toString()}` : "",
          },
          { replace: true },
        );
        toast.error(
          labels.login?.bankId?.error ||
            "BankID lyckades men sessionen kunde inte återställas. Försök logga in igen.",
        );
      }
      return;
    }

    void (async () => {
      try {
        // Never await close on the critical path — Android can hang if Custom Tabs
        // already dismissed via the deep link (blocks exchange → forever logged out).
        void closeBankIdSystemBrowser();
        console.info("[BankID native exchange] start");
        let inflight = inflightNativeCodeExchanges.get(nativeCode);
        if (!inflight) {
          inflight = AuthService.exchangeNativeBankIdCode(nativeCode).finally(() => {
            inflightNativeCodeExchanges.delete(nativeCode);
          });
          inflightNativeCodeExchanges.set(nativeCode, inflight);
        }
        const result = await inflight;
        markNativeCodeConsumed(nativeCode);
        // Clear prior 401/refreshBlocked state so the new session is not blocked.
        resetNativeAuthClientState();
        saveNativeTokens(result.nativeTokens);

        const details = await UserService.getUserDetails();
        const usernameParts = (details.username || "").trim().split(/\s+/);
        const accountTermsAccepted = details.accountTermsAccepted ?? false;
        authContext?.login(
          {
            id: details._id,
            firstName: details.firstName || usernameParts[0] || details.username || "",
            lastName: details.lastName || usernameParts.slice(1).join(" ") || "",
            email: details.email,
            type: mapApiUserTypeToUserType(details.role),
            phone: details.phoneNumber ?? undefined,
            emailVerified: details.emailVerified ?? true,
            accountTermsAccepted,
            otherTermsAccepted: details.otherTermsAccepted ?? false,
          },
          details.companyId ?? null,
        );
        queryClient.clear();

        if (result.flow === "register") {
          bankIdRegisterToastShown.current = true;
          markBankIdToastShown(BANKID_REGISTER_TOAST_KEY);
          toast.success(
            labels.bankIdRegister?.success || "Konto skapat med BankID",
          );
        } else {
          bankIdSuccessToastShown.current = true;
          markBankIdToastShown(BANKID_LOGIN_TOAST_KEY);
          toast.success(labels.login?.bankId?.success || "Inloggad med BankID");
        }

        // Navigate explicitly — do not rely on a later effect, and do not
        // navigate back to /auth in finally (that undid auto-login before).
        const userType = mapApiUserTypeToUserType(details.role);
        if (accountTermsAccepted === false && userType !== UserType.Admin) {
          navigate(buildRedirectAwarePath("/accept-terms", postAuthRedirect), {
            replace: true,
          });
        } else {
          navigate(postAuthRedirect, { replace: true });
        }
      } catch (err: any) {
        console.error("[BankID native exchange] failed", err);
        // Parallel/strict remount may have already completed exchange successfully.
        if (getNativeTokens()?.accessToken) {
          navigate(postAuthRedirect, { replace: true });
          return;
        }
        // One-time codes are dead after a failed/consumed attempt — don't replay on Back.
        markNativeCodeConsumed(nativeCode);
        const message =
          err?.message ||
          labels.login?.bankId?.error ||
          "BankID-inloggning misslyckades.";
        const errorCode = String(err?.errorCode || err?.code || "");
        const isSessionConflict =
          errorCode === "TOKEN_BLACKLISTED" ||
          errorCode === "SESSION_INVALID" ||
          errorCode === "SESSION_INVALIDATED" ||
          /token invalidated|blacklist|session invalid|invalid refresh/i.test(
            String(message),
          );
        const isExpiredReplay =
          /invalid or expired native bankid code/i.test(String(message));
        toast.error(
          isSessionConflict
            ? labels.login?.bankId?.sessionConflict ||
                "Något gick fel med sessionen efter BankID. Vänta någon minut och försök logga in igen."
            : isExpiredReplay
              ? labels.login?.bankId?.error ||
                "BankID lyckades men sessionen kunde inte återställas. Försök logga in igen."
              : message,
        );
        const next = new URLSearchParams(location.search);
        next.delete("nativeCode");
        next.delete("bankid");
        next.delete("bankidRegister");
        next.delete("t");
        navigate(
          {
            pathname: location.pathname,
            search: next.toString() ? `?${next.toString()}` : "",
          },
          { replace: true },
        );
      }
    })();
    // Intentionally keyed on search string so HashRouter deep links re-run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, location.search]);

  // Native: backend finished BankID but app opened without nativeCode (HTTPS
  // App Link / lost deep link) — surface instead of a silent /auth screen.
  useEffect(() => {
    if (!isNative || isLoading || isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    if (params.get("nativeCode")) return;
    const registerOk = params.get("bankidRegister") === "success";
    const loginOk = params.get("bankid") === "success";
    if (!registerOk && !loginOk) return;

    const timer = window.setTimeout(() => {
      if (getNativeTokens()?.accessToken) return;
      toast.error(
        registerOk
          ? labels.bankIdRegister?.error ||
              "Kontot skapades men appen kunde inte logga in dig. Stäng appen och logga in med BankID."
          : labels.login?.bankId?.error ||
              "BankID lyckades men appen kunde inte logga in dig. Försök igen.",
      );
      const next = new URLSearchParams(location.search);
      next.delete("bankidRegister");
      next.delete("bankid");
      navigate(
        {
          pathname: location.pathname,
          search: next.toString() ? `?${next.toString()}` : "",
        },
        { replace: true },
      );
    }, 1200);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, isLoading, isAuthenticated, location.search]);

  const bankIdErrorCopy = useMemo(() => {
    const bankId = labels.login?.bankId;
    const errors = bankId?.errors;
    if (bankIdErrorReason === "no_account") {
      return {
        title: errors?.noAccountTitle ?? bankId?.errorTitle ?? "Inget konto hittades",
        description:
          errors?.noAccount ??
          "BankID-autentiseringen lyckades, men vi har inget konto som är verifierat med det personnummer BankID returnerade.",
      };
    }
    if (bankIdErrorReason === "registration_pending") {
      return {
        title:
          errors?.registrationPendingTitle ??
          "Kontoförfrågan under granskning",
        description:
          errors?.registrationPending ??
          "BankID lyckades, men din kontoförfrågan väntar fortfarande på admin-godkännande.",
      };
    }
    return {
      title: errors?.unavailableTitle ?? bankId?.errorTitle ?? "BankID-inloggning misslyckades",
      description:
        errors?.unavailable ??
        bankId?.error ??
        "BankID-inloggning misslyckades. Det krävs ett befintligt konto som tidigare har autentiserats med BankID.",
    };
  }, [bankIdErrorReason, labels.login?.bankId]);

  const bankIdRegisterErrorCopy = useMemo(() => {
    const reg = labels.bankIdRegister;
    const errors = reg?.errors;
    if (bankIdRegisterErrorReason === "email_exists") {
      return {
        title: errors?.emailExistsTitle ?? reg?.errorTitle ?? "E-post används redan",
        description:
          errors?.emailExists ??
          "Det finns redan ett konto med den e-postadress du angav.",
      };
    }
    if (bankIdRegisterErrorReason === "email_pending") {
      return {
        title:
          errors?.emailPendingTitle ?? "Kontoförfrågan under granskning",
        description:
          errors?.emailPending ??
          "Det finns redan en pågående kontoförfrågan med den e-postadressen. Vänta på admin-godkännande.",
      };
    }
    if (bankIdRegisterErrorReason === "ssn_exists") {
      return {
        title: errors?.ssnExistsTitle ?? reg?.errorTitle ?? "Personnummer används redan",
        description:
          errors?.ssnExists ??
          "Det finns redan ett konto kopplat till personnumret från BankID.",
      };
    }
    if (bankIdRegisterErrorReason === "ssn_pending") {
      return {
        title:
          errors?.ssnPendingTitle ?? "Kontoförfrågan under granskning",
        description:
          errors?.ssnPending ??
          "Det finns redan en pågående kontoförfrågan med personnumret från BankID. Vänta på admin-godkännande.",
      };
    }
    return {
      title: errors?.unavailableTitle ?? reg?.errorTitle ?? "BankID-registrering misslyckades",
      description:
        errors?.unavailable ??
        reg?.error ??
        "BankID-registrering misslyckades. Försök igen senare.",
    };
  }, [bankIdRegisterErrorReason, labels.bankIdRegister]);

  useEffect(() => {
    if (!isNative) return;
    document.documentElement.classList.add("bilenia-native-auth-active");
    return () => {
      document.documentElement.classList.remove("bilenia-native-auth-active");
    };
  }, [isNative]);

  // Redirect to requested destination if user is already authenticated
  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;

    if (bankIdLoginFailed.current || bankIdRegisterFailed.current) {
      return;
    }

    // Native exchange already toasts; sessionStorage only for native Back-replay.
    // Web must always show the BankID login toast (sessionStorage was blocking it).
    if (
      searchParams.get("bankid") === "success" &&
      !bankIdSuccessToastShown.current &&
      !searchParams.get("nativeCode") &&
      !(isNative && wasBankIdToastShown(BANKID_LOGIN_TOAST_KEY))
    ) {
      bankIdSuccessToastShown.current = true;
      if (isNative) markBankIdToastShown(BANKID_LOGIN_TOAST_KEY);
      toast.success(labels.login?.bankId?.success || "Inloggad med BankID");
    }

    if (
      searchParams.get("bankidRegister") === "success" &&
      !bankIdRegisterToastShown.current &&
      !searchParams.get("nativeCode") &&
      !(isNative && wasBankIdToastShown(BANKID_REGISTER_TOAST_KEY))
    ) {
      bankIdRegisterToastShown.current = true;
      if (isNative) markBankIdToastShown(BANKID_REGISTER_TOAST_KEY);
      toast.success(
        labels.bankIdRegister?.success || "Konto skapat med BankID",
      );
    }

    if (user.emailVerified === false) {
      // Unverified password sessions are no longer issued; clear any stale session.
      void AuthService.logout().catch(() => undefined);
      authContext?.logout();
      queryClient.clear();
      return;
    }

    if (user.accountTermsAccepted === false && user.type !== UserType.Admin) {
      navigate(buildRedirectAwarePath("/accept-terms", postAuthRedirect), {
        replace: true,
      });
      return;
    }

    navigate(postAuthRedirect, { replace: true });
  }, [
    isAuthenticated,
    isLoading,
    navigate,
    postAuthRedirect,
    user,
    authContext,
    queryClient,
    labels.login?.bankId?.success,
    labels.bankIdRegister?.success,
    location.search,
    isNative,
  ]);

  const loginLabel = labels.login?.loading ?? "Loading...";
  if (isLoading || (isNative && isNativeSessionPending)) {
    if (isNative) {
      return <NativeAppSkeleton />;
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
          <p className="mt-4 text-sm text-gray-600">{loginLabel}</p>
        </div>
      </div>
    );
  }

  if (
    isAuthenticated &&
    !bankIdLoginFailed.current &&
    !bankIdRegisterFailed.current
  ) {
    return null;
  }

  return (
    <div
      className={
        isNative
          ? "native-auth-screen min-h-0 flex flex-col items-stretch relative overflow-hidden"
          : "min-h-screen flex flex-col md:flex-row items-center relative overflow-hidden"
      }
    >
      <RainBackground />

      {isNative ? (
        <div className="native-auth-screen-inner w-full flex flex-col items-stretch z-10 flex-1 min-h-0">
          <div className="native-auth-news-slot shrink-0">
            <MobileNewsSection compact />
          </div>
          <div className="native-auth-form-slot flex-1 min-h-0 overflow-hidden">
            <AuthCard />
          </div>
        </div>
      ) : isMobile ? (
        <div className="w-full flex flex-col items-center justify-center p-4 z-10 my-auto gap-6">
          <MobileNewsSection />
          <AuthCard />
        </div>
      ) : (
        <>
          <NewsSection />
          <Separator orientation="vertical" className="h-[400px] bg-white/10" />
          <div className="w-1/2 flex items-center justify-center p-4">
            <AuthCard />
          </div>
        </>
      )}

      <AlertDialog
        open={bankIdErrorOpen}
        onOpenChange={(open) => {
          setBankIdErrorOpen(open);
          if (!open) {
            setBankIdErrorReason(null);
            lastHandledBankIdSearch.current = null;
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle>{bankIdErrorCopy.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed pb-1">
              {bankIdErrorCopy.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!mt-6 pt-2">
            <AlertDialogAction>
              {labels.login?.bankId?.errorOk ?? "OK"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={bankIdRegisterErrorOpen}
        onOpenChange={(open) => {
          setBankIdRegisterErrorOpen(open);
          if (!open) {
            setBankIdRegisterErrorReason(null);
            lastHandledBankIdSearch.current = null;
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle>{bankIdRegisterErrorCopy.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed pb-1">
              {bankIdRegisterErrorCopy.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!mt-6 pt-2">
            <AlertDialogAction>
              {labels.bankIdRegister?.errorOk ?? "OK"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={bankIdRegisterPendingOpen}
        onOpenChange={setBankIdRegisterPendingOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle>
              {labels.bankIdRegister?.pendingTitle ?? "Ansökan skickad"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed pb-1">
              {labels.bankIdRegister?.pendingDescription ??
                "Ditt konto har skapats med BankID och väntar på admin-godkännande. Du får ett mejl när det är klart."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!mt-6 pt-2">
            <AlertDialogAction>
              {labels.bankIdRegister?.errorOk ?? "OK"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={openInAppPromptOpen}
        onOpenChange={setOpenInAppPromptOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {labels.login?.bankId?.openInAppTitle ?? "Öppna Bilenia-appen"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              {labels.login?.bankId?.openInAppDescription ??
                "BankID är klar. Öppna Bilenia-appen för att slutföra inloggningen."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                const deepLink = buildNativeAppAuthDeepLink(location.search);
                window.location.href = deepLink;
              }}
            >
              {labels.login?.bankId?.openInAppCta ?? "Öppna appen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Auth;
